import type {
  DailyTacticalReadiness,
  DailyTacticalWeeklyAudit,
  MarketStatusSimple,
} from '../../services/operationsApi';

const REQUIRED_REHEARSAL_RUNS = 3;

export default function Atu2RehearsalPanel({
  readiness,
  market,
  audit,
  auditError,
}: Props) {
  const completed = readiness.cohorts.rehearsal_run_count;
  const remaining = Math.max(REQUIRED_REHEARSAL_RUNS - completed, 0);
  const modelOutput = readiness.model_output_audit;
  return (
    <section className="atu2-panel">
      <div className="atu2-section-head">
        <div>
          <span className="atu2-kicker">사전 리허설 상태</span>
          <h2>다음 실행 전 확인</h2>
        </div>
        <p>read-only GET API 값만 표시합니다.</p>
      </div>
      <div className="atu2-rehearsal-grid">
        <StatusBox
          label="다음 사용 가능 window"
          value={windowValue(market)}
          detail={windowDetail(market)}
        />
        <StatusBox
          label="리허설 잔여"
          value={remaining > 0 ? `${remaining}회 남음` : '필수 횟수 충족'}
          detail={`현재 ${completed}/${REQUIRED_REHEARSAL_RUNS}회 · 공식 시작 전 기준`}
        />
        <StatusBox
          label="모델 출력 상태"
          value={modelOutputValue(modelOutput)}
          detail={modelOutputDetail(modelOutput)}
        />
        <StatusBox
          label="감사 상태"
          value={auditValue(audit, auditError)}
          detail={auditDetail(audit, auditError)}
        />
      </div>
    </section>
  );
}

function StatusBox({ label, value, detail }: StatusBoxProps) {
  return (
    <article className="atu2-rehearsal-box">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function windowValue(market: MarketStatusSimple) {
  if (market.is_open) return '지금 정규장 열림';
  if (market.next_event) return market.next_event;
  return '데이터 없음';
}

function windowDetail(market: MarketStatusSimple) {
  const wait = market.hours_until_next === null
    ? '대기 시간 데이터 없음'
    : `약 ${market.hours_until_next}시간 후`;
  return `${market.status_text} · 현재 ET: ${market.current_time_et} · ${wait}`;
}

function modelOutputValue(audit?: DailyTacticalReadiness['model_output_audit']) {
  if (!audit) return '데이터 없음';
  if (audit.checked_run_count === 0) return '점검 표본 없음';
  const valid = audit.status_counts.valid_json || 0;
  return audit.fallback_or_unrecorded_count === 0
    ? `valid_json ${valid}건`
    : `확인 필요 ${audit.fallback_or_unrecorded_count}건`;
}

function modelOutputDetail(audit?: DailyTacticalReadiness['model_output_audit']) {
  if (!audit) return 'readiness API에 model_output_audit 데이터가 없습니다.';
  const counts = Object.entries(audit.status_counts)
    .map(([status, count]) => `${status} ${count}`)
    .join(', ');
  return counts || '상태 카운트 데이터 없음';
}

function auditValue(audit: DailyTacticalWeeklyAudit | null, error: string | null) {
  if (error) return 'API 오류';
  if (!audit) return '데이터 없음';
  const passed = [
    audit.storage_integrity_audit,
    audit.fact_check_audit,
  ].filter((section) => section.passed).length;
  return `${passed}/2 통과`;
}

function auditDetail(audit: DailyTacticalWeeklyAudit | null, error: string | null) {
  if (error) return error;
  if (!audit) return 'weekly-audit 조회 결과가 없습니다.';
  const modelRerun = audit.model_rerun_audit.rows.length > 0
    ? passLabel(audit.model_rerun_audit.passed)
    : '명시 수동 감사 필요';
  return `대상 ${audit.cohort_id} · storage ${passLabel(audit.storage_integrity_audit.passed)} · fact ${passLabel(audit.fact_check_audit.passed)} · model rerun ${modelRerun}`;
}

function passLabel(passed: boolean) {
  return passed ? '통과' : '미통과';
}

type Props = {
  readiness: DailyTacticalReadiness;
  market: MarketStatusSimple;
  audit: DailyTacticalWeeklyAudit | null;
  auditError: string | null;
};

type StatusBoxProps = {
  label: string;
  value: string;
  detail: string;
};
